import type { EntityId } from './ids.js';
import type { CaptureItem, CaptureItemInput, ReviewOutcome } from './capture.js';
import type { Task, TaskInput, TaskLane } from './task.js';
import type { Reminder, ReminderInput, ReminderState } from './reminder.js';
import type { Review, ReviewInput } from './review.js';
import type { ConsistencyEvidence, ConsistencySummary, Target, TargetInput, TargetStatus } from './target.js';
import type {
  Habit,
  HabitCheckin,
  HabitInput,
  HabitStatus,
  HabitSummary,
  Routine,
  RoutineInput,
} from './routine.js';
import type { Problem, ProblemInput, ProblemPatch, ProblemStatus } from './problem.js';
import type { Project, ProjectInput, ProjectPatch, ProjectStatus } from './project.js';
import type {
  CalendarEvent,
  EventInput,
  EventOccurrence,
  EventPatch,
  EventStatus,
} from './event.js';
import type {
  Account,
  AccountInput,
  Transaction,
  TransactionInput,
  ImportBatch,
  ImportRow,
  FinanceSummary,
} from './finance.js';
import type { KnowledgeGraph, Note, NoteInput, NotePatch } from './note.js';
import type {
  Goal,
  GoalInput,
  GoalPatch,
  GoalProgress,
  GoalStatus,
  Milestone,
  MilestoneInput,
  MilestoneStatus,
  DirectionItem,
  DirectionItemInput,
  DirectionLens,
} from './direction.js';
import type { ControlCategory } from './problem.js';
import type { HealthLog, HealthLogInput, HealthSummary } from './health.js';
import type { Brief } from './assistant.js';
import type {
  StudySubject,
  StudySubjectInput,
  StudySession,
  StudySessionInput,
  StudySummary,
  MasteryState,
  SubjectStatus,
} from './study.js';
import type {
  Opportunity,
  OpportunityInput,
  OpportunityPatch,
  OpportunityStage,
} from './career.js';
import type { JournalEntry, JournalEntryInput, JournalStream, JournalSummary } from './journal.js';
import type {
  ContentItem,
  ContentItemInput,
  ContentItemPatch,
  ContentStage,
} from './content.js';
import type { Venture, VentureInput, VenturePatch, VentureStatus } from './business.js';
import type {
  InventoryItem,
  InventoryItemInput,
  InventoryItemPatch,
  ItemStatus,
} from './environment.js';
import type { FocusSession, FocusSessionInput, FocusSummary } from './focus.js';
import type {
  Profile,
  ProfileInput,
  Admission,
  AdmissionInput,
  AdmissionStatus,
  OfferStatus,
  FundingSource,
  FundingSourceInput,
  FundingStatus,
  TouchpointChannel,
  EducationProgram,
  EducationProgramInput,
} from './academics.js';
import type {
  ResumeProfile,
  ResumeProfileInput,
} from './resume.js';
import type { LockStatus, SecurityConfig, SecurityEvent } from './security.js';

/** Capture: fast, classification-free intake that preserves source first. */
export interface CaptureService {
  captureItem(input: CaptureItemInput): Promise<CaptureItem>;
  listUnreviewed(): Promise<readonly CaptureItem[]>;
  reviewCapture(id: EntityId, decision: ReviewOutcome): Promise<CaptureItem>;
}

/** Tasks: the Now/Next/Waiting/Scheduled/Done execution loop. */
export interface TasksService {
  create(input: TaskInput): Promise<Task>;
  listByLane(lane: TaskLane): Promise<readonly Task[]>;
  move(id: EntityId, lane: TaskLane, scheduledFor?: string | null): Promise<Task>;
  complete(id: EntityId): Promise<Task>;
}

export interface AgendaItem {
  readonly id: string;
  readonly title: string;
  /** ISO time the item is anchored at. */
  readonly at: string;
  readonly kind: 'scheduled_task' | 'reminder' | 'event';
}

/** A calm, capped daily view model. Never an all-domain dashboard. */
export interface TodayPlan {
  readonly date: string;
  readonly now: readonly Task[];
  readonly next: readonly Task[];
  readonly agenda: readonly AgendaItem[];
  /** Capped (<= 3) actionable review prompts. */
  readonly reviewPrompts: readonly ReviewPrompt[];
}

export interface ReviewPrompt {
  readonly id: string;
  readonly label: string;
}

export interface TodayService {
  buildTodayPlan(date: string): Promise<TodayPlan>;
}

export interface ReviewService {
  record(input: ReviewInput): Promise<Review>;
  listForDate(date: string): Promise<readonly Review[]>;
  listRecent(limit: number): Promise<readonly Review[]>;
}

export interface RemindersService {
  schedule(reminder: ReminderInput): Promise<Reminder>;
  listFiringOn(date: string): Promise<readonly Reminder[]>;
  /** All reminders, newest fire time first. */
  listAll(): Promise<readonly Reminder[]>;
  setState(id: EntityId, state: ReminderState): Promise<Reminder>;
}

/** The growth engine: measured commitments + consistency evidence (not shame). */
export interface TargetsService {
  create(input: TargetInput): Promise<Target>;
  list(): Promise<readonly Target[]>;
  /** Log one piece of evidence for the current moment. */
  logEvidence(targetId: EntityId, note?: string): Promise<ConsistencyEvidence>;
  consistency(targetId: EntityId): Promise<ConsistencySummary>;
  setStatus(targetId: EntityId, status: TargetStatus): Promise<Target>;
}

/** Routines & Habits: schedule-based behaviours (streaks) + ordered step blocks. */
export interface RoutinesService {
  createHabit(input: HabitInput): Promise<Habit>;
  listHabits(): Promise<readonly Habit[]>;
  checkIn(habitId: EntityId, note?: string): Promise<HabitCheckin>;
  habitSummary(habitId: EntityId): Promise<HabitSummary>;
  setHabitStatus(habitId: EntityId, status: HabitStatus): Promise<Habit>;
  createRoutine(input: RoutineInput): Promise<Routine>;
  listRoutines(): Promise<readonly Routine[]>;
  markRoutineRan(routineId: EntityId): Promise<Routine>;
  setRoutineStatus(routineId: EntityId, status: HabitStatus): Promise<Routine>;
}

/** Problems & Repair: diagnose limiters neutrally; turn fixable limits into growth. */
export interface ProblemsService {
  create(input: ProblemInput): Promise<Problem>;
  list(): Promise<readonly Problem[]>;
  update(id: EntityId, patch: ProblemPatch): Promise<Problem>;
  setStatus(id: EntityId, status: ProblemStatus): Promise<Problem>;
}

/** Projects: one canonical home per body of work; idea → proof, one next action. */
export interface ProjectsService {
  create(input: ProjectInput): Promise<Project>;
  list(): Promise<readonly Project[]>;
  update(id: EntityId, patch: ProjectPatch): Promise<Project>;
  setStatus(id: EntityId, status: ProjectStatus): Promise<Project>;
}

/** Calendar Events: time-anchored, optionally recurring; expanded into occurrences. */
export interface EventsService {
  create(input: EventInput): Promise<CalendarEvent>;
  list(): Promise<readonly CalendarEvent[]>;
  update(id: EntityId, patch: EventPatch): Promise<CalendarEvent>;
  setStatus(id: EntityId, status: EventStatus): Promise<CalendarEvent>;
  /** Occurrences overlapping the given UTC day. */
  occurrencesOn(dateIso: string): Promise<readonly EventOccurrence[]>;
  /** Occurrences overlapping [startIso, endIso]. */
  occurrencesBetween(startIso: string, endIso: string): Promise<readonly EventOccurrence[]>;
}

/** Finance Vault: accounts + signed transactions in exact minor units; vault-gated. */
export interface FinanceService {
  createAccount(input: AccountInput): Promise<Account>;
  listAccounts(): Promise<readonly Account[]>;
  recordTransaction(input: TransactionInput): Promise<Transaction>;
  listTransactions(accountId?: EntityId): Promise<readonly Transaction[]>;
  /** Current balance of one account in minor units. */
  balanceOf(accountId: EntityId): Promise<number>;
  /** Total net worth across all accounts in minor units. */
  netWorth(): Promise<number>;
  /** Income/expense/net + per-category over an inclusive date range. */
  summary(fromIso: string, toIso: string): Promise<FinanceSummary>;
  /** Idempotently ingest a normalized statement; re-imports never duplicate. */
  importStatement(args: {
    accountId: EntityId;
    sourceLabel: string;
    sourceFiles: readonly string[];
    rows: readonly ImportRow[];
  }): Promise<{ created: number; skipped: number; batch: ImportBatch }>;
  listImportBatches(): Promise<readonly ImportBatch[]>;
}

/** Notes: block documents with tags, [[wiki links]], and a derived graph. */
export interface NotesService {
  create(input: NoteInput): Promise<Note>;
  list(): Promise<readonly Note[]>;
  get(id: EntityId): Promise<Note | undefined>;
  update(id: EntityId, patch: NotePatch): Promise<Note>;
  /** Archive (never delete) a note. */
  archive(id: EntityId): Promise<Note>;
  /** The directed knowledge graph derived from all notes' wiki links. */
  graph(): Promise<KnowledgeGraph>;
}

/** Direction: Vision → Goal → Milestone. Milestones can serve many goals. */
export interface DirectionService {
  createGoal(input: GoalInput): Promise<Goal>;
  listGoals(): Promise<readonly Goal[]>;
  updateGoal(id: EntityId, patch: GoalPatch): Promise<Goal>;
  setGoalStatus(id: EntityId, status: GoalStatus): Promise<Goal>;
  createMilestone(input: MilestoneInput): Promise<Milestone>;
  listMilestones(): Promise<readonly Milestone[]>;
  setMilestoneStatus(id: EntityId, status: MilestoneStatus): Promise<Milestone>;
  /** Link/unlink a milestone to a goal (graph edge, not duplication). */
  linkMilestoneToGoal(milestoneId: EntityId, goalId: EntityId): Promise<Milestone>;
  /** Progress of one goal derived from its contributing milestones. */
  progressOf(goalId: EntityId): Promise<GoalProgress>;
  /** Direction sub-lens items: roles, values/mindsets, challenges/opportunities. */
  addItem(input: DirectionItemInput): Promise<DirectionItem>;
  listItems(lens: DirectionLens): Promise<readonly DirectionItem[]>;
  setItemPosture(id: EntityId, posture: ControlCategory | null): Promise<DirectionItem>;
  archiveItem(id: EntityId): Promise<DirectionItem>;
}

/** Health & Body: calm daily wellness check-ins (signals, never medical truth). */
export interface HealthService {
  log(input: HealthLogInput): Promise<HealthLog>;
  list(): Promise<readonly HealthLog[]>;
  /** Neutral averages over an inclusive date range. */
  summary(fromIso: string, toIso: string): Promise<HealthSummary>;
}

/** Assistant: a calm, read-only daily brief composed from current Life OS state. */
export interface AssistantService {
  brief(dateIso: string): Promise<Brief>;
}

/** Study: active subjects with mastery progression + logged practice sessions. */
export interface StudyService {
  createSubject(input: StudySubjectInput): Promise<StudySubject>;
  listSubjects(): Promise<readonly StudySubject[]>;
  setMastery(id: EntityId, state: MasteryState): Promise<StudySubject>;
  setSubjectStatus(id: EntityId, status: SubjectStatus): Promise<StudySubject>;
  logSession(input: StudySessionInput): Promise<StudySession>;
  listSessions(subjectId?: EntityId): Promise<readonly StudySession[]>;
  summary(fromIso: string, toIso: string): Promise<StudySummary>;
}

/** Career: the opportunity pipeline (saved → applied → interviewing → offer → closed). */
export interface CareerService {
  create(input: OpportunityInput): Promise<Opportunity>;
  list(): Promise<readonly Opportunity[]>;
  update(id: EntityId, patch: OpportunityPatch): Promise<Opportunity>;
  setStage(id: EntityId, stage: OpportunityStage): Promise<Opportunity>;
}

/** Journal: private reflective entries for the Mental and Spiritual streams. */
export interface JournalService {
  add(input: JournalEntryInput): Promise<JournalEntry>;
  list(stream: JournalStream): Promise<readonly JournalEntry[]>;
  summary(stream: JournalStream, fromIso: string, toIso: string): Promise<JournalSummary>;
}

/** Work & Content: the content production pipeline (idea → published). */
export interface ContentService {
  create(input: ContentItemInput): Promise<ContentItem>;
  list(): Promise<readonly ContentItem[]>;
  update(id: EntityId, patch: ContentItemPatch): Promise<ContentItem>;
  setStage(id: EntityId, stage: ContentStage): Promise<ContentItem>;
}

/** Business: owner-level venture portfolio (idea → experiment → active → …). */
export interface BusinessService {
  create(input: VentureInput): Promise<Venture>;
  list(): Promise<readonly Venture[]>;
  update(id: EntityId, patch: VenturePatch): Promise<Venture>;
  setStatus(id: EntityId, status: VentureStatus): Promise<Venture>;
}

/** Environment: workspace, devices, tools, and inventory. */
export interface EnvironmentService {
  create(input: InventoryItemInput): Promise<InventoryItem>;
  list(): Promise<readonly InventoryItem[]>;
  update(id: EntityId, patch: InventoryItemPatch): Promise<InventoryItem>;
  setStatus(id: EntityId, status: ItemStatus): Promise<InventoryItem>;
}

/** Focus & Pomodoro: logged deep-work sessions as execution evidence. */
export interface FocusService {
  logSession(input: FocusSessionInput): Promise<FocusSession>;
  list(): Promise<readonly FocusSession[]>;
  summary(fromIso: string, toIso: string): Promise<FocusSummary>;
}

/** Academics & Funding: profile, education, university admissions, funding sources. */
export interface AcademicsService {
  getProfile(): Promise<Profile | undefined>;
  saveProfile(input: ProfileInput): Promise<Profile>;
  createAdmission(input: AdmissionInput): Promise<Admission>;
  listAdmissions(): Promise<readonly Admission[]>;
  setOfferStatus(id: EntityId, offerStatus: OfferStatus): Promise<Admission>;
  setAdmissionStatus(id: EntityId, status: AdmissionStatus): Promise<Admission>;
  addFunding(input: FundingSourceInput): Promise<FundingSource>;
  listFunding(): Promise<readonly FundingSource[]>;
  setFundingStatus(id: EntityId, status: FundingStatus): Promise<FundingSource>;
  setFundingNextAction(id: EntityId, nextAction: string, nextActionDate: string | null): Promise<FundingSource>;
  logTouchpoint(id: EntityId, touchpoint: { on: string; channel: TouchpointChannel; note: string }): Promise<FundingSource>;
  importEducationProgram(input: EducationProgramInput): Promise<EducationProgram>;
  listEducationPrograms(): Promise<readonly EducationProgram[]>;
  importResumeProfile(input: ResumeProfileInput): Promise<ResumeProfile>;
  getResumeProfile(): Promise<ResumeProfile | undefined>;
}

/** Security & Lock: local passphrase lock, idle policy, vault step-up, audit. */
export interface SecurityService {
  status(): Promise<LockStatus>;
  getConfig(): Promise<SecurityConfig>;
  setPassphrase(passphrase: string): Promise<void>;
  clearPassphrase(passphrase: string): Promise<boolean>;
  unlock(passphrase: string): Promise<boolean>;
  lock(reason?: 'manual' | 'auto'): Promise<void>;
  setAutoLockMinutes(minutes: number): Promise<void>;
  setRequireStepUpForVault(required: boolean): Promise<void>;
  listAudit(limit: number): Promise<readonly SecurityEvent[]>;
}

/** The composed set of daily-loop services the app shell consumes. */
export interface FeatureServices {
  readonly capture: CaptureService;
  readonly tasks: TasksService;
  readonly today: TodayService;
  readonly reviews: ReviewService;
  readonly reminders: RemindersService;
  readonly targets: TargetsService;
  readonly routines: RoutinesService;
  readonly problems: ProblemsService;
  readonly projects: ProjectsService;
  readonly events: EventsService;
  readonly finance: FinanceService;
  readonly notes: NotesService;
  readonly direction: DirectionService;
  readonly health: HealthService;
  readonly assistant: AssistantService;
  readonly study: StudyService;
  readonly career: CareerService;
  readonly journal: JournalService;
  readonly content: ContentService;
  readonly business: BusinessService;
  readonly environment: EnvironmentService;
  readonly focus: FocusService;
  readonly academics: AcademicsService;
  readonly security: SecurityService;
}

/** Cap a list of review prompts to keep Today calm. */
export const MAX_REVIEW_PROMPTS = 3;
